import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddMemberDialog from '@/components/team/AddMemberDialog';
import AddPrivilegedMemberDialog from '@/components/team/AddPrivilegedMemberDialog';
import EditMemberDialog from '@/components/team/EditMemberDialog';
import EmployeeQRCard from '@/components/team/EmployeeQRCard';
import PageAccessDialog from '@/components/team/PageAccessDialog';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'react-hot-toast';

import { Mail, Phone, KeyRound, QrCode, Pencil, Trash2, ShieldCheck, Search, Plus, Eye, EyeOff, ChevronUp
} from 'lucide-react';

import { 
  ROLE_ORDER, ROLE_LABEL as ROLE_LABEL_MAP, canViewDeveloper, checkIsDev, checkIsOwner, getPageAccess
} from '@/lib/AuthSession';

export default function WorkspaceTeam() {
  const { member: currentMember } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Guard access based on page_access config
  useEffect(() => {
    if (!currentMember) return;
    const access = getPageAccess(currentMember, 'manajemen_tim');
    const isAdminOrPrivileged = checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember.role === 'admin';
    if (access === 'none' && !isAdminOrPrivileged) {
      navigate('/office');
    }
  }, [currentMember, navigate]);

  // Search & Filters
  const [teamSearch, setTeamSearch] = useState('');
  const [teamRoleFilter, setTeamRoleFilter] = useState('Semua');
  const [teamStatusFilter, setTeamStatusFilter] = useState('Semua');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => { setCurrentPage(1); }, [teamSearch, teamRoleFilter, teamStatusFilter]);

  // Popup Dialog States
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [showAddDeveloper, setShowAddDeveloper] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [qrMember, setQrMember] = useState(null);
  const [showPassIds, setShowPassIds] = useState({});
  const [accessMember, setAccessMember] = useState(null);

  // Fetch Team Members
  const { data: teamMembers = [], isLoading: isLoadingTeam } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.list("-join_date"),
  });

  const handleTeamDelete = async (id) => {
    const target = teamMembers.find(m => m.id === id);
    const name = target?.full_name || 'anggota tim ini';
    if (!confirm(`Yakin ingin menghapus ${name}?\n(Data akan dipindahkan ke Keranjang Sampah)`)) return;
    try {
      await base44.entities.TeamMember.delete(id, currentMember?.full_name || 'Admin');
      toast.success(`${name} berhasil dipindahkan ke Keranjang Sampah.`);
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
    } catch (e) {
      console.error(e);
      toast.error("Gagal menghapus anggota tim.");
    }
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Pindahkan ${selectedIds.size} anggota tim terpilih ke Keranjang Sampah?`)) return;
    await Promise.all(Array.from(selectedIds).map(id => base44.entities.TeamMember.delete(id)));
    clearSelection();
    queryClient.invalidateQueries({ queryKey: ["team-members"] });
  };

  const togglePass = (id) => setShowPassIds((p) => ({ ...p, [id]: !p[id] }));

  const getPageAccessSummary = (m) => {
    const pa = m.page_access || {};
    const keys = Object.keys(pa).filter(k => pa[k] !== "none");
    if (keys.length === 0) return null;
    const editCount = keys.filter(k => pa[k] === "edit").length;
    const viewCount = keys.filter(k => pa[k] === "view").length;
    const parts = [];
    if (editCount > 0) parts.push(`${editCount} edit`);
    if (viewCount > 0) parts.push(`${viewCount} lihat`);
    return parts.join(", ");
  };

  // Filter team members
  const filteredTeam = teamMembers
    .filter(m => {
      const q = teamSearch.toLowerCase();
      const matchSearch = !q || m.full_name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.employee_id?.toLowerCase().includes(q);
      const matchRole = teamRoleFilter === "Semua" || m.role === teamRoleFilter;
      const matchStatus = teamStatusFilter === "Semua" || m.status === teamStatusFilter;
      return matchSearch && matchRole && matchStatus;
    })
    .sort((a, b) => (ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)) || (a.full_name || "").localeCompare(b.full_name || ""));

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    selectedCount,
    totalCount,
    startSelection
  } = useBatchSelection(filteredTeam.filter(m => m.id !== currentMember?.id && m.role !== 'owner'));

  return (
    <div className="space-y-6 text-[#302621]">
      
      {/* Header Manajemen Tim */}
      <div className="flex flex-wrap justify-between items-center gap-4 border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-2xl font-black text-[#302621] tracking-tight">Manajemen Tim & Hak Akses</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Kelola data staff pabrik, konfigurasi pin/sandi pengaman, serta otorisasi modul per halaman.</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {(checkIsDev(currentMember) || checkIsOwner(currentMember)) && (
            <Button variant="outline" onClick={() => setShowAddOwner(true)} className="h-9 gap-1.5 text-xs border-yellow-300 text-yellow-700 hover:bg-yellow-50 font-bold rounded-xl">
              <ShieldCheck className="w-4 h-4 text-yellow-600" /> Tambah Owner
            </Button>
          )}
          {(checkIsDev(currentMember) || checkIsOwner(currentMember)) && (
            <Button variant="outline" onClick={() => setShowAddDeveloper(true)} className="h-9 gap-1.5 text-xs border-slate-300 text-slate-800 hover:bg-slate-50 font-bold rounded-xl">
              <ShieldCheck className="w-4 h-4" /> Tambah Dev
            </Button>
          )}
          <Button onClick={() => setShowAddDialog(true)} className="h-9 gap-1.5 text-xs bg-[#302621] hover:bg-slate-800 text-white font-bold rounded-xl">
            <Plus className="w-4 h-4" /> Tambah Anggota
          </Button>
        </div>
      </div>

      {/* Filter Tim */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A39171]" />
          <Input placeholder="Cari nama, email, ID..." className="pl-8 h-9 text-xs w-full sm:w-48 bg-white focus-visible:ring-[#8C6208]" value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)} />
        </div>
        <Select value={teamRoleFilter} onValueChange={setTeamRoleFilter}>
          <SelectTrigger className="h-9 w-36 text-xs bg-white border-[#E6D5B8]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Semua">Semua Role</SelectItem>
            <SelectItem value="owner">Owner</SelectItem>
            <SelectItem value="developer">Developer</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="kepala">Kepala</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
          </SelectContent>
        </Select>
        <Select value={teamStatusFilter} onValueChange={setTeamStatusFilter}>
          <SelectTrigger className="h-9 w-28 text-xs bg-white border-[#E6D5B8]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Semua">Semua Status</SelectItem>
            <SelectItem value="aktif">Aktif</SelectItem>
            <SelectItem value="nonaktif">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isSelectionMode && (
        <BatchActionBar 
          selectedCount={selectedCount}
          totalCount={totalCount}
          onSelectAll={selectAll}
          onClear={clearSelection}
          actions={[
            {
              label: 'Hapus Terpilih',
              icon: <Trash2 className="w-4 h-4" />,
              onClick: handleBatchDelete,
              variant: 'destructive'
            }
          ]}
        />
      )}

      {/* Daftar Anggota Tim (Kategori per Hierarki) */}
      {isLoadingTeam ? (
        <div className="text-center py-12 text-[#7A695B] text-xs">Memuat data tim...</div>
      ) : filteredTeam.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-xs">Tidak ada anggota tim ditemukan.</div>
      ) : (
        <div className="space-y-6">
          {Array.from(new Set(filteredTeam.map(m => m.department || "Tanpa Divisi"))).sort((a, b) => {
            if (a === "Direktur Utama") return -1;
            if (b === "Direktur Utama") return 1;
            if (a === "Developer") return 1;
            if (b === "Developer") return -1;
            return a.localeCompare(b);
          }).map(dept => {
            const groupMembers = filteredTeam.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).filter(m => (m.department || "Tanpa Divisi") === dept);
            if (groupMembers.length === 0) return null;
            
            return (
              <div key={dept} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-4 h-4 text-[#8C6208]" />
                  <h3 className="text-sm font-black text-[#302621] uppercase tracking-widest">{dept}</h3>
                </div>
                
                <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl overflow-hidden shadow-sm">
                  <div className="divide-y divide-[#E6D5B8]/30">
                    {groupMembers.map(m => {
                      const accessSummary = getPageAccessSummary(m);
                      const isDevHidden = m.role === 'developer' && !canViewDeveloper(currentMember);
                      return (
                        <div key={m.id} className={`p-4 sm:p-5 transition-colors flex flex-col sm:flex-row sm:items-center gap-4 ${selectedIds.has(m.id) ? 'bg-amber-50/50' : 'hover:bg-[#FFFDF9]'}`}>
                          
                          {/* Left: Avatar & Main Info */}
                          <div className="flex items-center gap-4 flex-1">
                            {/* Checkbox for batch selection */}
                            {m.id !== currentMember?.id && m.role !== 'owner' && isSelectionMode && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox 
                                  checked={selectedIds.has(m.id)}
                                  onCheckedChange={() => toggleSelection(m.id)}
                                  className="mr-2 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                                />
                              </div>
                            )}

                            <div className={`relative w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-base font-black shadow-sm overflow-hidden
                              ${m.role === "owner" ? "bg-gradient-to-br from-yellow-200 to-yellow-400 text-yellow-900 border border-yellow-300" :
                                m.role === "developer" ? "bg-gradient-to-br from-slate-800 to-black text-white" :
                                m.role === "admin" ? "bg-gradient-to-br from-red-100 to-red-200 text-red-800 border border-red-300" :
                                "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 border border-slate-300"}`}>
                              {isDevHidden ? "?" : (
                                m.photo_url ? (
                                  <img src={m.photo_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  m.full_name?.charAt(0)?.toUpperCase()
                                )
                              )}
                            </div>
                            
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className="font-black text-[#302621] text-sm truncate">
                                  {isDevHidden ? <span className="italic text-slate-400">— Tersembunyi —</span> : m.full_name}
                                </p>
                                {m.birth_date && !isDevHidden && (
                                  <div className="flex gap-1 items-center bg-fuchsia-50 border border-fuchsia-100 px-1.5 py-0.5 rounded text-[9px] font-bold text-fuchsia-700 whitespace-nowrap">
                                    <span>
                                      {(() => {
                                          const d = new Date(m.birth_date);
                                          let age = new Date().getFullYear() - d.getFullYear();
                                          const mth = new Date().getMonth() - d.getMonth();
                                          if (mth < 0 || (mth === 0 && new Date().getDate() < d.getDate())) { age--; }
                                          return age;
                                      })()} th
                                    </span>
                                    <span>•</span>
                                    <span>{
                                      (() => {
                                        const b = new Date(m.birth_date);
                                        const y = b.getFullYear();
                                        if (y >= 1946 && y <= 1964) return 'Baby Boomer';
                                        if (y >= 1965 && y <= 1980) return 'Gen X';
                                        if (y >= 1981 && y <= 1996) return 'Millennial';
                                        if (y >= 1997 && y <= 2012) return 'Gen Z';
                                        return 'Gen Alpha';
                                      })()
                                    }</span>
                                    <span>•</span>
                                    <span>{
                                      (() => {
                                        const b = new Date(m.birth_date);
                                        const mth = b.getMonth() + 1; const day = b.getDate();
                                        if ((mth === 3 && day >= 21) || (mth === 4 && day <= 19)) return 'Aries';
                                        if ((mth === 4 && day >= 20) || (mth === 5 && day <= 20)) return 'Taurus';
                                        if ((mth === 5 && day >= 21) || (mth === 6 && day <= 20)) return 'Gemini';
                                        if ((mth === 6 && day >= 21) || (mth === 7 && day <= 22)) return 'Cancer';
                                        if ((mth === 7 && day >= 23) || (mth === 8 && day <= 22)) return 'Leo';
                                        if ((mth === 8 && day >= 23) || (mth === 9 && day <= 22)) return 'Virgo';
                                        if ((mth === 9 && day >= 23) || (mth === 10 && day <= 22)) return 'Libra';
                                        if ((mth === 10 && day >= 23) || (mth === 11 && day <= 21)) return 'Scorpio';
                                        if ((mth === 11 && day >= 22) || (mth === 12 && day <= 21)) return 'Sagittarius';
                                        if ((mth === 12 && day >= 22) || (mth === 1 && day <= 19)) return 'Capricorn';
                                        if ((mth === 1 && day >= 20) || (mth === 2 && day <= 18)) return 'Aquarius';
                                        return 'Pisces';
                                      })()
                                    }</span>
                                  </div>
                                )}
                                <Badge variant={m.status === "aktif" ? "default" : "secondary"} className={`text-[9px] px-1.5 h-4 font-bold uppercase tracking-wider ${m.status === 'aktif' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}`}>
                                  {m.status}
                                </Badge>
                              </div>
                              <p className="text-[11px] font-semibold text-[#8C6208] uppercase tracking-widest">
                                {isDevHidden ? "" : [ROLE_LABEL_MAP[m.role] || m.role, m.department].filter(Boolean).join(" - ")}
                              </p>
                            </div>
                          </div>

                          {/* Middle: Contact & Access Details */}
                          <div className="flex-1 space-y-1.5 text-[11px] text-[#7A695B]">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
                              <span className="truncate">{isDevHidden ? "••••••••••••" : m.email}</span>
                            </div>
                            {m.phone && !isDevHidden && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-3.5 h-3.5 text-[#D4AF37]" />
                                <span>{m.phone}</span>
                              </div>
                            )}
                            {m.password && !isDevHidden && (
                              <div className="flex items-center gap-2 group">
                                <KeyRound className="w-3.5 h-3.5 text-[#D4AF37]" />
                                <span className="font-mono text-xs">{showPassIds[m.id] ? m.password : "••••••••"}</span>
                                <button onClick={() => togglePass(m.id)} className="text-[#A39171] hover:text-[#8C6208] ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {showPassIds[m.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Right: Actions */}
                          <div className="flex flex-wrap sm:flex-nowrap sm:flex-col sm:items-end gap-2 shrink-0">
                            <div className="flex gap-2 w-full sm:w-auto">
                              {m.employee_id && !isDevHidden && (
                                <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold gap-1.5 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl flex-1 sm:flex-none" onClick={() => setQrMember(m)}>
                                  <QrCode className="w-3.5 h-3.5" /> QR Card
                                </Button>
                              )}
                              {(!["owner", "developer"].includes(m.role) || (checkIsDev(currentMember) && m.role === 'owner')) && (
                                <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold gap-1.5 border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl flex-1 sm:flex-none" onClick={() => setAccessMember(m)}>
                                  <ShieldCheck className="w-3.5 h-3.5" /> Akses
                                </Button>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 w-full sm:w-auto sm:justify-end">
                              {(m.id !== currentMember?.id || checkIsDev(currentMember) || checkIsOwner(currentMember)) && !isDevHidden && (
                                <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold text-[#7A695B] hover:text-[#302621] hover:bg-slate-100 rounded-xl" onClick={() => setEditMember(m)}>
                                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                                </Button>
                              )}
                              {m.id !== currentMember?.id && m.role !== 'owner' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 text-[11px] font-bold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl" 
                                  onClick={(e) => { e.stopPropagation(); handleTeamDelete(m.id); }}
                                  title="Hapus Anggota Tim"
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus
                                </Button>
                              )}
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* ── Pagination Controls ── */}
          {filteredTeam.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filteredTeam.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredTeam.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredTeam.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* POPUPS & DIALOGS */}
      <AddMemberDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["team-members"] })}
      />

      <AddPrivilegedMemberDialog
        open={showAddOwner}
        roleToAdd="owner"
        onClose={() => setShowAddOwner(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["team-members"] })}
      />

      <AddPrivilegedMemberDialog
        open={showAddDeveloper}
        roleToAdd="developer"
        onClose={() => setShowAddDeveloper(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["team-members"] })}
      />

      <EditMemberDialog
        open={!!editMember}
        member={editMember}
        onClose={() => setEditMember(null)}
        onSaved={() => { queryClient.invalidateQueries({ queryKey: ["team-members"] }); setEditMember(null); }}
      />

      <EmployeeQRCard
        open={!!qrMember}
        member={qrMember}
        onClose={() => setQrMember(null)}
      />

      <PageAccessDialog
        open={!!accessMember}
        member={accessMember}
        onClose={() => setAccessMember(null)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["team-members"] });
          setAccessMember(null);
        }}
      />

    </div>
  );
}
